import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Search, Plus, FileText, Share2, Star } from "lucide-react";
import { categories, predefinedChecklists } from "../../data/checklistsData";

import Footer from "../../components/Footer";
import Navbar from "../../components/Navbar";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Meta from "../../components/Meta";

const Checklists = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [customChecklists, setCustomChecklists] = useState([]);
  const [checklistProgress, setChecklistProgress] = useState({});

  useEffect(() => {
    const allCustomChecklists = Object.keys(localStorage)
      .filter((key) => key.startsWith("custom-checklist-"))
      .map((key) => JSON.parse(localStorage.getItem(key)));
    setCustomChecklists(allCustomChecklists);

    const progress = {};
    [...predefinedChecklists, ...allCustomChecklists].forEach((checklist) => {
      const saved = localStorage.getItem(`checklist-progress-${checklist.id}`);
      if (saved) {
        progress[checklist.id] = JSON.parse(saved);
      }
    });
    setChecklistProgress(progress);
  }, []);

  const getProgress = (checklistId, itemsCount) => {
    const completed = Object.values(
      checklistProgress[checklistId] || {}
    ).filter(Boolean).length;
    return Math.round((completed / itemsCount) * 100);
  };

  const shareChecklist = (checklistId) => {
    const url = `${window.location.origin}/checklists/${checklistId}`;
    navigator.clipboard.writeText(url);
    toast.success("Checklist link copied to clipboard!", {
      position: "bottom-right",
      autoClose: 3000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
    });
  };

  const allAvailableChecklists = [...predefinedChecklists];

  const filteredChecklists = allAvailableChecklists.filter((checklist) => {
    const matchesSearch =
      checklist.name &&
      checklist.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory =
      selectedCategory === "all" || checklist.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const featuredFiltered = filteredChecklists.filter((c) => c.featured);
  const nonFeaturedFiltered = filteredChecklists.filter((c) => !c.featured);

  const showFeaturedSection = !searchTerm && selectedCategory === "all";

  return (
    <>
      <Meta
        title="Checklists - Govind Sinha & Co."
        description="Find all the checklists you need for your financial and legal documentation."
        canonical="https://cagovindsinha.com/checklists"
      />

      <div className="min-h-screen pt-16 bg-gradient-to-br from-blue-50 to-indigo-100">
        <Navbar />
        <div className="container px-4 py-8 mx-auto max-w-7xl">
          <div className="mb-8 text-center">
            <h1 className="mb-2 text-4xl font-bold text-gray-900">
              Document Checklists
            </h1>
            <p className="text-lg text-gray-600">
              Streamline your document collection process
            </p>
          </div>

          {/* Search and Filter */}
          <div className="p-6 mb-8 bg-white rounded-lg shadow-md">
            <div className="flex flex-col gap-4 md:flex-row">
              <div className="relative flex-1">
                <Search className="absolute w-4 h-4 text-gray-400 left-3 top-3" />
                <input
                  type="text"
                  placeholder="Search checklists..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full py-2 pl-10 pr-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <Link
                to="/checklists/custom/new"
                // onClick={() => window.scrollTo(0, 0)}
                className="flex items-center justify-center px-4 py-2 text-white transition-colors bg-blue-600 rounded-lg hover:bg-blue-700 whitespace-nowrap"
              >
                <Plus className="w-4 h-4 mr-2" /> Create Custom
              </Link>
            </div>
          </div>

          {/* Categories */}
          <div className="flex flex-wrap gap-2 mb-8">
            {categories.map((category) => {
              const Icon = category.icon;
              return (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                    selectedCategory === category.id
                      ? "bg-blue-600 text-white"
                      : "bg-white text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {category.name}
                </button>
              );
            })}
          </div>
          {/* Featured Checklists Section (only when no search/category filter) */}
          {showFeaturedSection && featuredFiltered.length > 0 && (
            <div className="mb-8">
              <h2 className="flex items-center mb-4 text-2xl font-bold text-gray-900">
                <Star
                  className="w-6 h-6 mr-2 text-yellow-500"
                  fill="currentColor"
                />{" "}
                Featured Checklists
              </h2>
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
                {featuredFiltered.map((checklist) => {
                  const progress = getProgress(
                    checklist.id,
                    checklist.items.length
                  );
                  return (
                    <Link
                      key={checklist.id}
                      // onClick={() => window.scrollTo(0, 0)}
                      to={`/checklists/${checklist.slug}`}
                      className="p-6 transition-shadow bg-white border-2 border-yellow-400 rounded-lg shadow-md cursor-pointer hover:shadow-lg"
                    >
                      <div className="flex items-start justify-between mb-4">
                        <h3 className="text-xl font-semibold text-gray-900">
                          {checklist.name}
                        </h3>
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            shareChecklist(checklist.id);
                          }}
                          className="p-2 text-gray-400 rounded-full hover:text-blue-600 hover:bg-gray-100"
                        >
                          <Share2 className="w-4 h-4" />
                        </button>
                      </div>

                      <div className="mb-4">
                        <div className="flex justify-between mb-2 text-sm text-gray-600">
                          <span>{checklist.items.length} items</span>
                          <span>{progress}% complete</span>
                        </div>
                        <div className="w-full h-2 bg-gray-200 rounded-full">
                          <div
                            className="h-2 transition-all duration-300 bg-blue-600 rounded-full"
                            style={{ width: `${progress}%` }}
                          ></div>
                        </div>
                      </div>

                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-500 capitalize">
                          {checklist.category}
                        </span>
                        <span className="text-sm font-medium text-blue-600">
                          {
                            Object.values(
                              checklistProgress[checklist.id] || {}
                            ).filter(Boolean).length
                          }{" "}
                          / {checklist.items.length}
                        </span>
                      </div>
                    </Link>
                  );
                })}
              </div>
            </div>
          )}

          {/* Checklists Grid */}
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            {(showFeaturedSection
              ? nonFeaturedFiltered
              : filteredChecklists
            ).map((checklist) => {
              const progress = getProgress(
                checklist.id,
                checklist.items.length
              );
              return (
                <Link
                  key={checklist.id}
                  // onClick={() => window.scrollTo(0, 0)}
                  to={`/checklists/${checklist.slug}`}
                  className={`bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow p-6 cursor-pointer ${
                    checklist.featured && !showFeaturedSection
                      ? "border-2 border-yellow-400"
                      : ""
                  }`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <h3 className="text-xl font-semibold text-gray-900">
                      {checklist.name}
                    </h3>
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        shareChecklist(checklist.id);
                      }}
                      className="p-2 text-gray-400 rounded-full hover:text-blue-600 hover:bg-gray-100"
                    >
                      <Share2 className="w-4 h-4" />
                    </button>
                  </div>

                  <div className="mb-4">
                    <div className="flex justify-between mb-2 text-sm text-gray-600">
                      <span>{checklist.items.length} items</span>
                      <span>{progress}% complete</span>
                    </div>
                    <div className="w-full h-2 bg-gray-200 rounded-full">
                      <div
                        className="h-2 transition-all duration-300 bg-blue-600 rounded-full"
                        style={{ width: `${progress}%` }}
                      ></div>
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500 capitalize">
                      {checklist.category}
                    </span>
                    <span className="text-sm font-medium text-blue-600">
                      {
                        Object.values(
                          checklistProgress[checklist.id] || {}
                        ).filter(Boolean).length
                      }{" "}
                      / {checklist.items.length}
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>

          {filteredChecklists.length === 0 && (
            <div className="py-12 text-center">
              <FileText className="w-16 h-16 mx-auto mb-4 text-gray-400" />
              <h3 className="mb-2 text-lg font-medium text-gray-900">
                No checklists found
              </h3>
              <p className="text-gray-600">
                Try adjusting your search or create a custom checklist
              </p>
            </div>
          )}
        </div>
      </div>
      <Footer />
      <ToastContainer />
    </>
  );
};

export default Checklists;
